import { useMemo, useState } from 'react';
import { fetchDealerships } from '../../../api/fetchDealerships';
import { getAllRegionals, getCellsByRegional, getAllCells } from '../../../data/cellRegionalTraceability';
import { VEHICLE_MODEL_TYPE_LABEL_ID, type VehicleModelType } from '../../../data/vehicleModelType';
import type { Dealership } from '../../../models/dealership';
import SearchAutocomplete from '../../form/searchAutocomplete';
import {
    UptimeChecklist,
    UptimeDateRange,
    UptimeDrawer,
    UptimeFilterGroup,
    UptimeFilterPills,
    UptimeSelect,
} from '../../shared';
import {
    DAY_PRESETS,
    TYPE_OPTIONS,
    getDayPresetInputs,
    getStepLabelId,
    matchDayPreset,
    toStepValue,
    toggleItem,
} from '../trackingUtils';
import useMtText from './useMtText';
import './TrackingMoreFilters.scss';

export type AlertFilterValue = 'all' | 'true' | 'false';

export interface MoreFiltersValue {
    alert: AlertFilterValue;
    regionals: string[];
    cells: string[];
    cpv: string;
    dealerships: Dealership[];
    modelTypes: VehicleModelType[];
    models: string[];
    types: string[];
    clients: string[];
    openFrom: string;
    openTo: string;
    updateFrom: string;
    updateTo: string;
    passedStep: string;
}

export const EMPTY_MORE_FILTERS: MoreFiltersValue = {
    alert: 'all',
    regionals: [],
    cells: [],
    cpv: '',
    dealerships: [],
    modelTypes: [],
    models: [],
    types: [],
    clients: [],
    openFrom: '',
    openTo: '',
    updateFrom: '',
    updateTo: '',
    passedStep: '',
};

const ALERT_OPTIONS: { value: AlertFilterValue; labelId: string }[] = [
    { value: 'all', labelId: 'intl-msg:kanban.filterRow.allFeminine' },
    { value: 'true', labelId: 'intl-msg:kanban.filterRow.yes' },
    { value: 'false', labelId: 'intl-msg:kanban.filterRow.no' },
];

interface IDateFilterProps {
    label: string;
    from: string;
    to: string;
    onChange: (range: { from: string; to: string }) => void;
}

const DateFilter = ({ label, from, to, onChange }: IDateFilterProps) => {
    const { t, msg } = useMtText();
    const preset = matchDayPreset(from, to);
    return (
        <UptimeFilterGroup label={label}>
            <UptimeFilterPills
                options={DAY_PRESETS.map(item => ({ value: item.key, label: msg(item.labelId) }))}
                isSelected={key => preset === key}
                onToggle={key => onChange(preset === key ? { from: '', to: '' } : getDayPresetInputs(key))}
            />
            <UptimeDateRange
                from={from}
                to={to}
                fromLabel={t('filters.from')}
                toLabel={msg('intl-msg:kanban.filterRow.until')}
                onChange={onChange}
            />
        </UptimeFilterGroup>
    );
};

interface ITrackingMoreFiltersProps {
    value: MoreFiltersValue;
    cpvOptions: string[];
    modelTypes: VehicleModelType[];
    modelOptions: string[];
    clientOptions: { name: string; count: number }[];
    stepKeys: string[];
    onApply: (value: MoreFiltersValue) => void;
    onClose: () => void;
}

const TrackingMoreFilters = ({
    value,
    cpvOptions,
    modelTypes,
    modelOptions,
    clientOptions,
    stepKeys,
    onApply,
    onClose,
}: ITrackingMoreFiltersProps) => {
    const { t, msg, formatNumber } = useMtText();
    const [draft, setDraft] = useState<MoreFiltersValue>(value);
    const [clientQuery, setClientQuery] = useState('');
    const [modelQuery, setModelQuery] = useState('');

    const update = (patch: Partial<MoreFiltersValue>) => setDraft(prev => ({ ...prev, ...patch }));

    const availableCells = useMemo(() => {
        if (!draft.regionals.length) {
            return getAllCells();
        }
        const cells = new Set<number>();
        for (const regional of draft.regionals) {
            for (const cell of getCellsByRegional(Number(regional))) {
                cells.add(cell);
            }
        }
        return [...cells].sort((a, b) => a - b);
    }, [draft.regionals]);

    const visibleClients = useMemo(() => {
        const term = clientQuery.trim().toLowerCase();
        return term ? clientOptions.filter(client => client.name.toLowerCase().includes(term)) : clientOptions;
    }, [clientOptions, clientQuery]);

    const visibleModels = useMemo(() => {
        const term = modelQuery.trim().toLowerCase();
        return term ? modelOptions.filter(model => model.toLowerCase().includes(term)) : modelOptions;
    }, [modelOptions, modelQuery]);

    const changeRegionals = (regionals: string[]) => {
        const valid = new Set(
            regionals.length
                ? regionals.flatMap(regional => getCellsByRegional(Number(regional)).map(String))
                : getAllCells().map(String)
        );
        update({ regionals, cells: draft.cells.filter(cell => valid.has(cell)) });
    };

    return (
        <UptimeDrawer
            className='maintenance-tracking'
            title={t('filters.more')}
            onClose={onClose}
            footer={
                <>
                    <button
                        type='button'
                        className='mt-btn'
                        onClick={() => {
                            setDraft(EMPTY_MORE_FILTERS);
                            setClientQuery('');
                            setModelQuery('');
                        }}
                    >
                        {t('filters.clear')}
                    </button>
                    <button type='button' className='mt-btn mt-primary' onClick={() => onApply(draft)}>
                        {t('filters.apply')}
                    </button>
                </>
            }
        >
            <div className='mt-filters'>
                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.regional')}>
                    <UptimeFilterPills
                        options={getAllRegionals().map(regional => ({ value: String(regional), label: regional }))}
                        isSelected={regional => draft.regionals.includes(regional)}
                        onToggle={regional => changeRegionals(toggleItem(draft.regionals, regional))}
                    />
                </UptimeFilterGroup>

                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.cell')}>
                    <UptimeFilterPills
                        options={availableCells.map(cell => ({ value: String(cell), label: cell }))}
                        isSelected={cell => draft.cells.includes(cell)}
                        onToggle={cell => update({ cells: toggleItem(draft.cells, cell) })}
                    />
                </UptimeFilterGroup>

                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.consultant')}>
                    <UptimeSelect
                        className='mt-fullselect'
                        value={draft.cpv}
                        options={[
                            { value: '', label: msg('intl-msg:kanban.filterRow.all') },
                            ...cpvOptions.map(cpv => ({ value: cpv, label: cpv })),
                        ]}
                        onChange={(cpv: string) => update({ cpv })}
                    />
                </UptimeFilterGroup>

                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.dealerships')}>
                    {draft.dealerships.length > 0 && (
                        <div className='mt-pills'>
                            {draft.dealerships.map(dealership => (
                                <span key={dealership.id} className='mt-chip'>
                                    {dealership.name}, DN {dealership.id}
                                    <button
                                        type='button'
                                        aria-label={t('chips.remove')}
                                        onClick={() =>
                                            update({
                                                dealerships: draft.dealerships.filter(
                                                    item => item.id !== dealership.id
                                                ),
                                            })
                                        }
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <SearchAutocomplete
                        id='mt-dealership-search'
                        placeholder={msg('intl-msg:kanban.filterRow.dealershipSearchPlaceholder')}
                        fetchOptions={async (term: string) => await fetchDealerships(term, term, false)}
                        optionLabel={{
                            id: 'DN',
                            name: msg('intl-msg:kanban.form.dealershipChildForm.optionName'),
                        }}
                        onSelectedOption={(dealership: Dealership) => {
                            if (!draft.dealerships.some(item => item.id === dealership.id)) {
                                update({ dealerships: [...draft.dealerships, dealership] });
                            }
                        }}
                        overlay
                        overlayClassName='!w-full'
                        clearAfterSelect
                        onClear={() => undefined}
                        valueRenderer={(option: Dealership) => `${option.name}, DN: ${option.id}`}
                    />
                </UptimeFilterGroup>

                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.category')}>
                    <UptimeFilterPills
                        options={modelTypes.map(type => ({
                            value: type,
                            label: msg(VEHICLE_MODEL_TYPE_LABEL_ID[type]),
                        }))}
                        isSelected={type => draft.modelTypes.includes(type)}
                        onToggle={type => update({ modelTypes: toggleItem(draft.modelTypes, type) })}
                    />
                </UptimeFilterGroup>

                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.models')}>
                    <UptimeChecklist
                        query={modelQuery}
                        onQueryChange={setModelQuery}
                        placeholder={msg('intl-msg:kanban.filterRow.modelSearchPlaceholder')}
                        items={visibleModels.map(model => ({ key: model, label: model }))}
                        selected={draft.models}
                        onToggle={model => update({ models: toggleItem(draft.models, model) })}
                        emptyText={msg('intl-msg:kanban.filterRow.noModelFound')}
                        clearLabel={t('filters.clearSelection', { count: draft.models.length })}
                        onClear={() => update({ models: [] })}
                        maxHeight={200}
                    />
                </UptimeFilterGroup>

                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.type')}>
                    <UptimeFilterPills
                        options={TYPE_OPTIONS.map(option => ({ value: option.value, label: msg(option.labelId) }))}
                        isSelected={type => draft.types.includes(type)}
                        onToggle={type => update({ types: toggleItem(draft.types, type) })}
                    />
                </UptimeFilterGroup>

                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.customer')}>
                    <UptimeChecklist
                        query={clientQuery}
                        onQueryChange={setClientQuery}
                        placeholder={t('filters.searchClient')}
                        items={visibleClients.map(client => ({
                            key: client.name,
                            label: client.name,
                            count: formatNumber(client.count),
                        }))}
                        selected={draft.clients}
                        onToggle={client => update({ clients: toggleItem(draft.clients, client) })}
                        emptyText={t('filters.noClient')}
                        hint={t('filters.clientHint')}
                        clearLabel={t('filters.clearSelection', { count: draft.clients.length })}
                        onClear={() => update({ clients: [] })}
                        maxHeight={200}
                    />
                </UptimeFilterGroup>

                <UptimeFilterGroup label={msg('intl-msg:kanban.filterRow.inAlert')}>
                    <UptimeFilterPills
                        options={ALERT_OPTIONS.map(option => ({ value: option.value, label: msg(option.labelId) }))}
                        isSelected={alert => draft.alert === alert}
                        onToggle={alert => update({ alert })}
                    />
                </UptimeFilterGroup>

                <DateFilter
                    label={msg('intl-msg:kanban.filterRow.openingDate')}
                    from={draft.openFrom}
                    to={draft.openTo}
                    onChange={({ from, to }) => update({ openFrom: from, openTo: to })}
                />

                <DateFilter
                    label={msg('intl-msg:kanban.filterRow.updateDate')}
                    from={draft.updateFrom}
                    to={draft.updateTo}
                    onChange={({ from, to }) => update({ updateFrom: from, updateTo: to })}
                />

                <UptimeFilterGroup label={t('filters.passedStep')}>
                    <UptimeSelect
                        className='mt-fullselect'
                        value={draft.passedStep}
                        options={[
                            { value: '', label: msg('intl-msg:kanban.filterRow.allFeminine') },
                            ...stepKeys.map(stepKey => ({
                                value: toStepValue(stepKey) as string,
                                label: msg(getStepLabelId(toStepValue(stepKey))),
                            })),
                        ]}
                        onChange={(passedStep: string) => update({ passedStep })}
                    />
                </UptimeFilterGroup>
            </div>
        </UptimeDrawer>
    );
};

export default TrackingMoreFilters;
